import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { useUser } from './UserContext';
import { toast } from 'react-toastify';

const BACKEND_API_URL = 'https://tripoptimizebackend.onrender.com';

// Styled Components
const ProfileContainer = styled.div`
  max-width: 800px;
  margin: 2rem auto;
  padding: 2rem;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  color: #2c3e50;
  margin-bottom: 2rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
`;

const SectionTitle = styled.h2`
  color: #34495e;
  font-size: 1.2rem;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #2c3e50;
  font-weight: 500;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.8rem;
  border: 2px solid #e9ecef;
  border-radius: 6px;
  font-size: 1rem;
  background-color: white;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const CheckboxGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.8rem;
  background: white;
  padding: 1rem;
  border-radius: 6px;
  border: 2px solid #e9ecef;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  padding: 0.5rem;
  cursor: pointer;
  user-select: none;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f8f9fa;
  }
`;

const Checkbox = styled.input`
  margin-right: 0.8rem;
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const Button = styled.button`
  background-color: #3498db;
  color: white;
  padding: 1rem 2rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  width: 100%;
  transition: all 0.2s;
  
  &:hover {
    background-color: #2980b9;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// Constants for select options
const TRAVEL_CONTEXTS = ['solo', 'family', 'partner', 'friends'];
const AGE_GROUPS = ['18-25', '25-34', '35-45', '46-55', '56-70', '70+'];
const ACTIVITIES = ['cultural', 'outdoor', 'shopping', 'food', 'nightlife', 'relaxation', 'adventure', 'sightseeing'];
const PACE_OPTIONS = ['relaxed', 'moderate', 'intense'];
const DIETARY_RESTRICTIONS = ['vegan', 'vegetarian', 'halal', 'kosher', 'gluten-free', 'dairy-free'];
const FOOD_ALLERGIES = ['nuts', 'shellfish', 'dairy', 'eggs', 'soy', 'wheat'];

const Profile = () => {
  const { user } = useUser();
  const [profile, setProfile] = useState({
    default_travel_context: '',
    age_group: '',
    preferred_activities: [],
    preferred_pace: '',
    accessibility_needs: '',
    dietary_restrictions: [],
    food_allergies: [],
    notification_preferences: {
      trip_reminders: true,
      price_alerts: true,
      travel_tips: true
    }
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${BACKEND_API_URL}/get-profile`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setProfile(response.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error('Error loading profile');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const endpoint = profile.id ? '/update-profile' : '/create-profile';
      const method = profile.id ? 'put' : 'post';
      
      await axios[method](`${BACKEND_API_URL}${endpoint}`, profile, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      
      toast.success('Profile saved successfully');
      fetchProfile();
    } catch (error) {
      toast.error('Error saving profile');
    }
  };

  const handleCheckboxGroup = (field, value) => {
    const currentValues = profile[field];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    setProfile({ ...profile, [field]: newValues });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile({ ...profile, [name]: value });
  };

  return (
    <ProfileContainer>
      <Title>Travel Preferences</Title>
      <form onSubmit={handleSubmit}>
        <Section>
          <SectionTitle>Travel Context</SectionTitle>
          <FormGroup>
            <Label>How do you usually travel?</Label>
            <Select
              name="default_travel_context"
              value={profile.default_travel_context}
              onChange={handleChange}
            >
              <option value="">Select travel context</option>
              {TRAVEL_CONTEXTS.map(context => (
                <option key={context} value={context}>
                  {context.charAt(0).toUpperCase() + context.slice(1)}
                </option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Age Group</Label>
            <Select
              name="age_group"
              value={profile.age_group}
              onChange={handleChange}
            >
              <option value="">Select age group</option>
              {AGE_GROUPS.map(group => (
                <option key={group} value={group}>{group}</option>
              ))}
            </Select>
          </FormGroup>
        </Section>

        <Section>
          <SectionTitle>Travel Preferences</SectionTitle>
          <FormGroup>
            <Label>Preferred Activities</Label>
            <CheckboxGroup>
              {ACTIVITIES.map(activity => (
                <CheckboxLabel key={activity}>
                  <Checkbox
                    type="checkbox"
                    checked={profile.preferred_activities.includes(activity)}
                    onChange={() => handleCheckboxGroup('preferred_activities', activity)}
                  />
                  {activity.charAt(0).toUpperCase() + activity.slice(1)}
                </CheckboxLabel>
              ))}
            </CheckboxGroup>
          </FormGroup>

          <FormGroup>
            <Label>Preferred Pace</Label>
            <Select
              name="preferred_pace"
              value={profile.preferred_pace}
              onChange={handleChange}
            >
              <option value="">Select preferred pace</option>
              {PACE_OPTIONS.map(pace => (
                <option key={pace} value={pace}>
                  {pace.charAt(0).toUpperCase() + pace.slice(1)}
                </option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Accessibility Needs</Label>
            <Select
              name="accessibility_needs"
              value={profile.accessibility_needs}
              onChange={handleChange}
            >
              <option value="">None</option>
              <option value="wheelchair">Wheelchair Accessible</option>
              <option value="limited_mobility">Limited Mobility</option>
              <option value="hearing_impaired">Hearing Impaired</option>
              <option value="visually_impaired">Visually Impaired</option>
            </Select>
          </FormGroup>
        </Section>

        <Section>
          <SectionTitle>Dietary Preferences</SectionTitle>
          <FormGroup>
            <Label>Dietary Restrictions</Label>
            <CheckboxGroup>
              {DIETARY_RESTRICTIONS.map(restriction => (
                <CheckboxLabel key={restriction}>
                  <Checkbox
                    type="checkbox"
                    checked={profile.dietary_restrictions.includes(restriction)}
                    onChange={() => handleCheckboxGroup('dietary_restrictions', restriction)}
                  />
                  {restriction.charAt(0).toUpperCase() + restriction.slice(1)}
                </CheckboxLabel>
              ))}
            </CheckboxGroup>
          </FormGroup>

          <FormGroup>
            <Label>Food Allergies</Label>
            <CheckboxGroup>
              {FOOD_ALLERGIES.map(allergy => (
                <CheckboxLabel key={allergy}>
                  <Checkbox
                    type="checkbox"
                    checked={profile.food_allergies.includes(allergy)}
                    onChange={() => handleCheckboxGroup('food_allergies', allergy)}
                  />
                  {allergy.charAt(0).toUpperCase() + allergy.slice(1)}
                </CheckboxLabel>
              ))}
            </CheckboxGroup>
          </FormGroup>
        </Section>

        <Button type="submit">Save Preferences</Button>
      </form>
    </ProfileContainer>
  );
};

export default Profile;